import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import AuthShell from '@/components/auth/AuthShell';
import { validateOrdinaryPostAuthNext } from '@/utils/authRedirect';

type CallbackStatus = 'loading' | 'success' | 'error';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [message, setMessage] = useState('이메일 인증을 확인하는 중이에요...');
  const handledRef = useRef(false);

  const run = useCallback(async () => {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));

    const authError =
      query.get('error_description') ||
      query.get('error') ||
      hash.get('error_description') ||
      hash.get('error');
    if (authError) {
      setStatus('error');
      setMessage(authError);
      return;
    }

    const next = validateOrdinaryPostAuthNext(query.get('next'));
    const code = query.get('code');

    if (code) {
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) {
        setStatus('error');
        setMessage(error.message);
        return;
      }
      if (data.redirectType === 'recovery') {
        navigate('/auth/reset-password', { replace: true });
        return;
      }
      await refreshProfile();
      setStatus('success');
      setMessage('가입확인이 완료되었습니다.');
      return;
    }

    const accessToken = hash.get('access_token');
    const refreshToken = hash.get('refresh_token');
    const hashType = hash.get('type');
    if (accessToken && refreshToken) {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) {
        setStatus('error');
        setMessage(error.message);
        return;
      }
      if (hashType === 'recovery') {
        navigate('/auth/reset-password', { replace: true });
        return;
      }
      await refreshProfile();
      setStatus('success');
      setMessage('가입확인이 완료되었습니다.');
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData.session) {
      await refreshProfile();
      setStatus('success');
      setMessage('가입확인이 완료되었습니다.');
      return;
    }

    setStatus('error');
    setMessage('인증 정보를 찾을 수 없어요. 인증 링크가 만료되었거나 이미 사용되었을 수 있어요.');
  }, [navigate, refreshProfile]);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    void run();
  }, [run]);

  const handleContinue = () => {
    navigate('/', { replace: true });
  };

  return (
    <AuthShell
      title={
        status === 'loading'
          ? '이메일 인증 확인 중'
          : status === 'success'
            ? '가입확인이 완료되었습니다'
            : '인증하지 못했어요'
      }
      subtitle={
        status === 'success'
          ? '이제 로그인해서 오답노트를 사용할 수 있어요. 운영자 승인 후 바로 이용 가능해요.'
          : status === 'error'
            ? '아래 내용을 확인한 뒤 다시 시도해 주세요.'
            : '잠시만 기다려 주세요.'
      }
      footer={
        status === 'error' ? (
          <>
            <Link to="/login" className="font-bold text-primary-700 hover:underline">
              로그인 화면으로
            </Link>
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {status === 'loading' && (
          <div className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-xs leading-relaxed text-foreground-700">
            <i className="ri-loader-4-line mt-0.5 animate-spin text-base text-secondary-700"></i>
            <span>{message}</span>
          </div>
        )}

        {status === 'success' && (
          <>
            <div className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-xs leading-relaxed text-foreground-700">
              <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-600"></i>
              <span>{message}</span>
            </div>
            <button
              type="button"
              onClick={handleContinue}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-arrow-right-line text-base"></i>
              계속하기
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex items-start gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-3 text-xs leading-relaxed text-accent-800">
              <i className="ri-error-warning-line mt-0.5 text-base"></i>
              <span>{message}</span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Link
                to="/login"
                className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
              >
                <i className="ri-login-box-line text-base"></i>
                로그인
              </Link>
              <Link
                to="/signup"
                className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
              >
                <i className="ri-user-add-line text-base"></i>
                다시 가입
              </Link>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}